import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Globe, Save, X } from 'lucide-react';
import { websiteAPI, cmsAPI, purchaseAPI } from '../../services/api';
import ImageCropperModal from '../common/ImageCropperModal';

// Website category list must match the storefront's category tabs
const WEB_CATEGORY_OPTIONS = ['Rings', 'Necklaces', 'Bangles', 'Kada', 'Earrings', 'Pendants', 'Bracelets', 'Anklets', 'Brooch', 'Watches', 'Accessories'];

const EMPTY_FORM = {
  showOnWebsite: false, webTitle: '', webDescription: '', displayPrice: '',
  discountPercent: 0, seoSlug: '', isFeatured: false, tileSize: 'normal',
  webCategory: '', collection: '', gender: 'women', sortOrder: 0, customFields: []
};

// The tag photo column holds whichever form the photo arrived in: a full URL,
// a data URI, a path under the API, or bare base64 with nothing to mark it.
const resolveTagImage = (raw) => {
  if (!raw) return null;
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
  if (raw.startsWith('/') || raw.includes('/uploads/')) {
    const host = process.env.REACT_APP_API_URL || 'http://localhost:5002';
    return `${host}${raw.startsWith('/') ? '' : '/'}${raw}`;
  }
  return raw.length > 30 ? `data:image/jpeg;base64,${raw}` : null;
};

const fmt2 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The "Publish Item to Public Website" popup.
 *
 * Shared by Purchase > Item View and Website CMS > Published Products so both
 * screens edit a piece through exactly the same form.
 *
 * piece: { purchaseDetailID, skuNo, itemName, salePrice, mrp, category, collection, showOnWebsite }
 */
export default function PublishItemModal({ piece, defaultTagImage = null, onClose, onSaved }) {
  const [webForm, setWebForm] = useState(EMPTY_FORM);
  const [webImages, setWebImages] = useState([]);
  // The tag photograph on the purchase line — what the website shows when
  // none of the website pictures is marked MAIN. Fetched here rather than
  // passed in, so every screen that opens this popup sees the same one.
  const [tagImage, setTagImage] = useState(null);
  const [webLoading, setWebLoading] = useState(false);
  const [webSaving, setWebSaving] = useState(false);
  const [dynamicCollections, setDynamicCollections] = useState(['Heritage', 'Aurora', 'Rivaaz', 'Solace', 'Ember', 'Everyday'].map((n) => ({ value: n, key: n, label: n })));
  // Which categories belong to each gender. The mega-menu sections already
  // carry this — page_men and page_women are the lists the website itself shows
  // under Men and Women — so the shop maintains it in one place rather than two.
  const [genderCategories, setGenderCategories] = useState({ men: [], women: [] });
  const [cropImageSrc, setCropImageSrc] = useState(null);

  // House collections come from the CMS "Collections Page" section
  useEffect(() => {
    const fetchCmsCollections = async () => {
      try {
        const res = await cmsAPI.getAllSections();
        const list = res.data?.data || res.data || [];
        // The mega-menu's own category lists, read here so this dropdown and the
        // website's Men / Women menus can never disagree
        const readMenu = (key) => {
          const sec = list.find((x) => x.sectionKey === key);
          if (!sec?.extraConfigJson) return [];
          try {
            const parsed = typeof sec.extraConfigJson === 'string'
              ? JSON.parse(sec.extraConfigJson) : sec.extraConfigJson;
            if (!Array.isArray(parsed)) return [];
            return parsed
              .filter((i) => i && i.isActive !== false)
              .map((i) => {
                // The label is what a shopper reads; the category is what the
                // catalogue is actually filtered by, and it rides in the link
                const m = /category=([^&]+)/.exec(i.href || '');
                return m ? decodeURIComponent(m[1]) : (i.label || '').trim();
              })
              .filter(Boolean);
          } catch { return []; }
        };

        setGenderCategories({ men: readMenu('page_men'), women: readMenu('page_women') });

        const colSection = list.find((s) => s.sectionKey === 'page_collections');
        if (colSection?.extraConfigJson) {
          const parsed = typeof colSection.extraConfigJson === 'string'
            ? JSON.parse(colSection.extraConfigJson)
            : colSection.extraConfigJson;
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Keyed on the slug, which is unique, rather than the name, which is
            // not: two cards may carry the same name and they are still two
            // collections. The name is what a piece is filed under, so a repeat
            // is shown with its slug to tell them apart.
            const seenName = new Map();
            parsed.forEach((c) => {
              const name = (c.name || c.title || '').trim();
              if (!name) return;
              seenName.set(name, (seenName.get(name) || 0) + 1);
            });

            const cards = parsed
              .map((c) => {
                const name = (c.name || c.title || '').trim();
                if (!name) return null;
                const slug = (c.slug || '').trim();
                return {
                  value: name,
                  key: slug || name,
                  label: seenName.get(name) > 1 && slug ? `${name}  (${slug})` : name,
                };
              })
              .filter(Boolean);

            if (cards.length > 0) setDynamicCollections(cards);
          }
        }
      } catch {}
    };
    fetchCmsCollections();
  }, []);

  // Only the categories the website actually shows for this gender. Unisex sees
  // both lists, because a unisex piece belongs under either menu. Whatever the
  // piece already carries stays on the list, so opening a piece never silently
  // changes what it was filed under.
  const categoryChoices = (() => {
    const g = (webForm.gender || 'women').toLowerCase();
    const fromCms = g === 'men'
      ? genderCategories.men
      : g === 'unisex'
        ? [...genderCategories.men, ...genderCategories.women]
        : genderCategories.women;

    const list = fromCms.length > 0 ? fromCms : WEB_CATEGORY_OPTIONS;
    const current = (webForm.webCategory || '').trim();
    return Array.from(new Set(current ? [current, ...list] : list));
  })();

  const loadWebsiteDetailsForPiece = async (purchaseDetailID, rowFallback) => {
    setWebLoading(true);
    try {
      const res = await websiteAPI.getWebsiteDetails(purchaseDetailID);
      const d = res.data?.data;
      if (d) {
        setWebForm({
          showOnWebsite: d.showOnWebsite || false,
          webTitle: d.webTitle || rowFallback?.itemName || '',
          webDescription: d.webDescription || '',
          displayPrice: d.displayPrice || rowFallback?.salePrice || rowFallback?.mrp || '',
          discountPercent: d.discountPercent || 0,
          seoSlug: d.seoSlug || '',
          isFeatured: d.isFeatured || false,
          tileSize: d.tileSize || 'normal',
          webCategory: d.webCategory || d.category || '',
          collection: d.collection || '',
          gender: d.gender || 'women',
          // Carried through untouched so saving never resets the CMS running order
          sortOrder: d.sortOrder || 0,
          customFields: d.customFields || []
        });
        setWebImages(d.images || []);
      }
    } catch {
      setWebForm({
        ...EMPTY_FORM,
        showOnWebsite: rowFallback?.showOnWebsite || false,
        webTitle: rowFallback?.itemName || '',
        displayPrice: rowFallback?.salePrice || rowFallback?.mrp || '',
        webCategory: rowFallback?.category || '',
        collection: ''
      });
      setWebImages([]);
    } finally {
      setWebLoading(false);
    }
  };

  useEffect(() => {
    if (piece?.purchaseDetailID) loadWebsiteDetailsForPiece(piece.purchaseDetailID, piece);
  }, [piece?.purchaseDetailID]);

  useEffect(() => {
    let cancelled = false;
    setTagImage(null);
    if (!piece?.purchaseDetailID) return undefined;

    (async () => {
      try {
        const res = await purchaseAPI.getDetailImage(piece.purchaseDetailID);
        const raw = res?.data?.data?.imageData;
        if (!cancelled) setTagImage(resolveTagImage(raw));
      } catch {
        if (!cancelled) setTagImage(null);
      }
    })();

    return () => { cancelled = true; };
  }, [piece?.purchaseDetailID]);

  const loadWebImagesOnly = async (purchaseDetailID) => {
    try {
      const res = await websiteAPI.getWebsiteDetails(purchaseDetailID);
      const d = res.data?.data;
      if (d) {
        setWebImages(d.images || []);
      }
    } catch {}
  };

  const handleSaveWebDetails = async (e) => {
    if (e) e.preventDefault();
    if (!piece) return;
    setWebSaving(true);
    try {
      await websiteAPI.saveWebsiteDetails(piece.purchaseDetailID, webForm);
      toast.success(webForm.showOnWebsite ? 'Published to website!' : 'Website details saved (hidden from catalog)');
      if (onSaved) onSaved(webForm);
      if (onClose) onClose();
    } catch {
      toast.error('Failed to save website details');
    } finally {
      setWebSaving(false);
    }
  };

  const handleUploadWebImage = async (filesInput) => {
    if (!filesInput || !piece) return;
    const fileArray = Array.from(filesInput.length !== undefined ? filesInput : [filesInput]);
    if (fileArray.length === 0) return;

    setWebLoading(true);
    try {
      const uploadPromises = fileArray.map(async (file, i) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mediaType', 'image');
        formData.append('isPrimary', false);
        return websiteAPI.uploadImage(piece.purchaseDetailID, formData);
      });

      const results = await Promise.allSettled(uploadPromises);
      const successCount = results.filter(r => r.status === 'fulfilled').length;

      if (successCount > 0) {
        toast.success(successCount + ' photo(s) uploaded!');
        await loadWebImagesOnly(piece.purchaseDetailID);
      } else {
        toast.error('Failed to upload image(s)');
      }
    } finally {
      setWebLoading(false);
    }
  };

  const handleDeleteWebImage = async (imageId, mediaUrl) => {
    if (!piece) return;
    try {
      if (imageId && imageId > 0) await websiteAPI.deleteImage(imageId);
      toast.success('Image removed!');
      setWebImages((prev) => prev.filter((x) => (imageId && imageId > 0 ? x.id !== imageId : x.mediaUrl !== mediaUrl)));
    } catch {
      toast.error('Failed to delete image');
    }
  };

  const handleSetPrimaryWebImage = async (imageId) => {
    if (!piece) return;
    try {
      await websiteAPI.setPrimaryImage(piece.purchaseDetailID, imageId || 0);
      toast.success('Main website thumbnail updated!');
      await loadWebImagesOnly(piece.purchaseDetailID);
    } catch {
      toast.error('Failed to set main thumbnail');
    }
  };

  const handleSetHoverWebImage = async (imageId) => {
    if (!piece) return;
    try {
      await websiteAPI.setHoverImage(piece.purchaseDetailID, imageId || 0);
      toast.success('Hover photo updated!');
      await loadWebImagesOnly(piece.purchaseDetailID);
    } catch {
      toast.error('Failed to set hover photo');
    }
  };

  if (!piece) return null;

  return (
    <>
      <div style={{
          position: 'fixed', inset: 0, background: 'rgba(8,12,28,0.75)', zIndex: 1200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(10px)', padding: 20
        }} onClick={() => onClose()}>
          <div style={{
            width: '100%', maxWidth: 940, maxHeight: '92vh', background: '#fff',
            borderRadius: 16, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }} onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              padding: '18px 24px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', color: '#fff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: webForm.showOnWebsite ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : '#475569',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Globe size={20} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
                    Publish Item to Public Website
                    {webForm.showOnWebsite ? (
                      <span style={{
                        fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 12,
                        background: (piece?.isSold || String(piece?.productStatus || '').toLowerCase() === 'sold') ? '#fee2e2' : '#dcfce7',
                        color: (piece?.isSold || String(piece?.productStatus || '').toLowerCase() === 'sold') ? '#b91c1c' : '#15803d',
                        border: `1px solid ${(piece?.isSold || String(piece?.productStatus || '').toLowerCase() === 'sold') ? '#fca5a5' : '#86efac'}`
                      }}>
                        {(piece?.isSold || String(piece?.productStatus || '').toLowerCase() === 'sold') ? '● SOLD OUT' : '● LIVE ON WEBSITE'}
                      </span>
                    ) : (
                      <span style={{
                        fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 12,
                        background: '#334155',
                        color: '#94a3b8',
                        border: '1px solid #475569'
                      }}>
                        ○ HIDDEN FROM CATALOG
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                    SKU: <strong>{piece.skuNo || '—'}</strong> &middot; Item: <strong>{piece.itemName || 'Jewellery Item'}</strong> &middot; Sale Price: <strong>₹{fmt2(piece.salePrice || piece.mrp)}</strong>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => onClose()}
                style={{ color: '#fff', opacity: 0.8 }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 24, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Header Toggle Row */}
              <div style={{
                background: webForm.showOnWebsite ? '#eff6ff' : '#f8fafc',
                border: `1.5px solid ${webForm.showOnWebsite ? '#93c5fd' : '#cbd5e1'}`,
                borderRadius: 12, padding: '14px 20px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                    Website Catalog Status
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {webForm.showOnWebsite
                      ? 'This piece is active and visible in the public e-commerce store catalog.'
                      : 'This piece is hidden from the public catalog.'}
                  </div>
                </div>

                <label style={{
                  display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none',
                  background: '#fff', padding: '8px 18px', borderRadius: 24, border: `1.5px solid ${webForm.showOnWebsite ? '#2563eb' : '#cbd5e1'}`,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: webForm.showOnWebsite ? '#1d4ed8' : '#64748b' }}>
                    {webForm.showOnWebsite ? 'Show on website ON' : 'Show on website OFF'}
                  </span>
                  <input
                    type="checkbox"
                    checked={webForm.showOnWebsite}
                    onChange={e => setWebForm(p => ({ ...p, showOnWebsite: e.target.checked }))}
                    style={{ width: 20, height: 20, cursor: 'pointer' }}
                  />
                </label>
              </div>

              {/* 2-Column Main Form Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20 }}>

                {/* Left Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Web Product Title</label>
                    <input
                      className="form-control"
                      placeholder="Web product title"
                      value={webForm.webTitle}
                      onChange={e => setWebForm(p => ({ ...p, webTitle: e.target.value }))}
                      style={{ height: 38, fontSize: 13.5, fontWeight: 600 }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Display Price (₹)</label>
                      <input
                        type="number" step="0.01"
                        className="form-control"
                        placeholder="Default: Sale Price"
                        value={webForm.displayPrice}
                        onChange={e => setWebForm(p => ({ ...p, displayPrice: e.target.value }))}
                        style={{ height: 38, fontSize: 14, fontWeight: 700, fontFamily: 'var(--mono)', color: '#be185d' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Discount %</label>
                      <input
                        type="number" step="0.5"
                        className="form-control"
                        placeholder="0"
                        value={webForm.discountPercent}
                        onChange={e => setWebForm(p => ({ ...p, discountPercent: e.target.value }))}
                        style={{ height: 38, fontSize: 13.5 }}
                      />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>SEO Slug</label>
                      <button
                        type="button"
                        onClick={() => {
                          const clean = (webForm.webTitle || piece?.itemName || 'item').toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');
                          setWebForm(p => ({ ...p, seoSlug: `${clean}-${piece?.purchaseDetailID}` }));
                        }}
                        style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Auto Generate
                      </button>
                    </div>
                    <input
                      className="form-control"
                      placeholder="necklace-123"
                      value={webForm.seoSlug}
                      onChange={e => setWebForm(p => ({ ...p, seoSlug: e.target.value }))}
                      style={{ height: 38, fontSize: 12.5, fontFamily: 'var(--mono)' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Website Category</label>
                      <select
                        className="form-control"
                        value={webForm.webCategory}
                        onChange={e => setWebForm(p => ({ ...p, webCategory: e.target.value }))}
                        style={{ height: 38, fontSize: 12.5 }}
                      >
                        <option value="">Auto (use ERP category)</option>
                        {categoryChoices.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 3 }}>
                        {webForm.gender === 'men' ? 'Categories shown under MEN'
                          : webForm.gender === 'unisex' ? 'Categories shown under MEN and WOMEN'
                          : 'Categories shown under WOMEN'}
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Worn By</label>
                      <select
                        className="form-control"
                        value={webForm.gender}
                        onChange={e => setWebForm(p => ({ ...p, gender: e.target.value }))}
                        style={{ height: 38, fontSize: 12.5 }}
                      >
                        <option value="women">Women</option>
                        <option value="men">Men</option>
                        <option value="unisex">Unisex</option>
                      </select>
                      <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 3 }}>
                        Drives Women / Men menu filters
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>✦ House Collection (Dynamic CMS)</span>
                    </label>
                    <select
                      className="form-control"
                      value={webForm.collection || ''}
                      onChange={e => setWebForm(p => ({ ...p, collection: e.target.value }))}
                      style={{ height: 38, fontSize: 13, fontWeight: 700, borderColor: '#3b82f6', background: '#f0f9ff' }}
                    >
                      <option value="">None / General Everyday</option>
                      {(webForm.collection && !dynamicCollections.some((c) => c.value === webForm.collection)) && (
                        // What the piece is filed under today, even if that
                        // collection has since been renamed or removed
                        <option value={webForm.collection}>{webForm.collection}</option>
                      )}
                      {dynamicCollections.map(c => (
                        <option key={c.key} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 3 }}>
                      Assigns piece to dynamic collection pages created in CMS Manager
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tile Display Size</label>
                      <select
                        className="form-control"
                        value={webForm.tileSize}
                        onChange={e => setWebForm(p => ({ ...p, tileSize: e.target.value }))}
                        style={{ height: 38, fontSize: 12.5 }}
                      >
                        <option value="normal">Normal Tile</option>
                        <option value="wide">Wide Banner</option>
                        <option value="tall">Tall Feature</option>
                        <option value="large">Large Highlight</option>
                      </select>
                    </div>
                    <div style={{ paddingTop: 18 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                        <input
                          type="checkbox"
                          checked={webForm.isFeatured}
                          onChange={e => setWebForm(p => ({ ...p, isFeatured: e.target.checked }))}
                          style={{ width: 18, height: 18 }}
                        />
                        <span>⭐ Featured Piece</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Web Product Description</label>
                    <textarea
                      rows={3}
                      className="form-control"
                      placeholder="Craftsmanship details, gemstone notes, or story for public website..."
                      value={webForm.webDescription}
                      onChange={e => setWebForm(p => ({ ...p, webDescription: e.target.value }))}
                      style={{ fontSize: 12.5, resize: 'vertical' }}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Extra Media Gallery */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                        📷 Photos & Videos ({webImages.length + 1} total)
                      </label>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <label style={{ background: '#2563eb', color: '#fff', padding: '5px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                          + Upload Multiple Photos
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={e => {
                              if (e.target.files && e.target.files.length > 0) {
                                if (e.target.files.length === 1) {
                                  const file = e.target.files[0];
                                  const objectUrl = URL.createObjectURL(file);
                                  setCropImageSrc(objectUrl);
                                } else {
                                  handleUploadWebImage(e.target.files);
                                }
                                e.target.value = '';
                              }
                            }}
                          />
                        </label>
                        <label style={{ background: '#0f172a', color: '#fff', padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                          ✂️ Crop Photo
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={e => {
                              if (e.target.files && e.target.files[0]) {
                                const file = e.target.files[0];
                                const objectUrl = URL.createObjectURL(file);
                                setCropImageSrc(objectUrl);
                                e.target.value = '';
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'thin' }}>
                      {/* Default Tag Image Card */}
                      {(() => {
                        const isTagPrimary = !webImages.some(x => x.isPrimary);
                        const isTagHover = !webImages.some(x => x.isHover);
                        return (
                          <div
                            style={{
                              width: 104,
                              height: 104,
                              borderRadius: 10,
                              background: '#fff',
                              border: isTagPrimary ? '2px solid #16a34a' : isTagHover ? '2px solid #8b5cf6' : '1px solid #cbd5e1',
                              boxShadow: isTagPrimary ? '0 0 0 2px rgba(22,163,74,0.25)' : isTagHover ? '0 0 0 2px rgba(139,92,246,0.25)' : 'none',
                              overflow: 'hidden',
                              position: 'relative',
                              flexShrink: 0
                            }}
                          >
                            {(defaultTagImage || tagImage) ? (
                              <img src={defaultTagImage || tagImage} alt="Default Tag" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center', paddingTop: 30 }}>Tag Photo</div>
                            )}
                            
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 1, background: 'rgba(15,23,42,0.85)', padding: '2px 0' }}>
                              <button
                                type="button"
                                onClick={() => handleSetPrimaryWebImage(0)}
                                title="Set Tag photo as main thumbnail"
                                style={{ flex: 1, border: 'none', background: isTagPrimary ? '#16a34a' : 'transparent', color: '#fff', fontSize: 8.5, fontWeight: 800, padding: '4px 0', cursor: 'pointer' }}
                              >
                                {isTagPrimary ? '★ MAIN' : 'SET MAIN'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSetHoverWebImage(0)}
                                title="Set Tag photo as hover photo"
                                style={{ flex: 1, border: 'none', background: isTagHover ? '#8b5cf6' : 'transparent', color: '#fff', fontSize: 8.5, fontWeight: 800, padding: '4px 0', cursor: 'pointer' }}
                              >
                                {isTagHover ? '✨ HOVER' : 'SET HOVER'}
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Extra Uploaded Images */}
                      {webImages.map((img, idx) => (
                        <div
                          key={img.id || img.mediaUrl || idx}
                          style={{
                            width: 104,
                            height: 104,
                            borderRadius: 10,
                            background: '#fff',
                            border: img.isPrimary ? '2px solid #16a34a' : img.isHover ? '2px solid #8b5cf6' : '1px solid #cbd5e1',
                            boxShadow: img.isPrimary ? '0 0 0 2px rgba(22,163,74,0.25)' : img.isHover ? '0 0 0 2px rgba(139,92,246,0.25)' : 'none',
                            overflow: 'hidden',
                            position: 'relative',
                            flexShrink: 0
                          }}
                        >
                          <img src={img.mediaUrl.startsWith('/') ? `${process.env.REACT_APP_API_URL || 'http://localhost:5002'}${img.mediaUrl}` : img.mediaUrl} alt="Extra" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          
                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDeleteWebImage(img.id, img.mediaUrl); }}
                            title="Delete photo"
                            style={{ position: 'absolute', top: 3, right: 3, background: 'rgba(239,68,68,0.9)', border: 'none', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
                          >
                            <X size={12} />
                          </button>

                          {/* Dual Action Buttons: SET MAIN and SET HOVER */}
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 1, background: 'rgba(15,23,42,0.85)', padding: '2px 0' }}>
                            <button
                              type="button"
                              onClick={() => handleSetPrimaryWebImage(img.id)}
                              title="Set as main thumbnail"
                              style={{ flex: 1, border: 'none', background: img.isPrimary ? '#16a34a' : 'transparent', color: '#fff', fontSize: 8.5, fontWeight: 800, padding: '4px 0', cursor: 'pointer' }}
                            >
                              {img.isPrimary ? '★ MAIN' : 'SET MAIN'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSetHoverWebImage(img.id)}
                              title="Set as hover photo on card"
                              style={{ flex: 1, border: 'none', background: img.isHover ? '#8b5cf6' : 'transparent', color: '#fff', fontSize: 8.5, fontWeight: 800, padding: '4px 0', cursor: 'pointer' }}
                            >
                              {img.isHover ? '✨ HOVER' : 'SET HOVER'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Custom Specifications */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <label style={{ fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                        📑 Custom Specifications
                      </label>
                      <button
                        type="button"
                        onClick={() => setWebForm(p => ({ ...p, customFields: [...(p.customFields || []), { fieldName: '', fieldValue: '' }] }))}
                        style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '5px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer' }}
                      >
                        + Add Field
                      </button>
                    </div>

                    {(webForm.customFields || []).length === 0 ? (
                      <div style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic', padding: '8px 0' }}>
                        No custom fields added (e.g. Gemstone: Diamond, Certificate: SGL)
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 180, overflowY: 'auto' }}>
                        {webForm.customFields.map((f, fIdx) => (
                          <div key={fIdx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                              className="form-control"
                              placeholder="Name (e.g. Gemstone)"
                              value={f.fieldName}
                              onChange={e => {
                                const val = e.target.value;
                                setWebForm(p => ({
                                  ...p,
                                  customFields: p.customFields.map((x, idx) => idx === fIdx ? { ...x, fieldName: val } : x)
                                }));
                              }}
                              style={{ height: 32, fontSize: 12, flex: 1 }}
                            />
                            <input
                              className="form-control"
                              placeholder="Value (e.g. Emerald 0.5ct)"
                              value={f.fieldValue}
                              onChange={e => {
                                const val = e.target.value;
                                setWebForm(p => ({
                                  ...p,
                                  customFields: p.customFields.map((x, idx) => idx === fIdx ? { ...x, fieldValue: val } : x)
                                }));
                              }}
                              style={{ height: 32, fontSize: 12, flex: 1 }}
                            />
                            <button
                              type="button"
                              onClick={() => setWebForm(p => ({ ...p, customFields: p.customFields.filter((_, idx) => idx !== fIdx) }))}
                              style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>

            </div>

            {/* Modal Footer */}
            <div style={{
              background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '14px 24px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => onClose()}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSaveWebDetails}
                disabled={webSaving}
                style={{
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  color: '#fff', border: 'none', padding: '10px 26px', borderRadius: 8,
                  fontWeight: 800, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: '0 4px 14px rgba(29,78,216,0.3)'
                }}
              >
                {webSaving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={16} />}
                {webForm.showOnWebsite ? 'Save & Publish to Website' : 'Save Website Details'}
              </button>
            </div>

          </div>
        </div>

        {cropImageSrc && (
          <ImageCropperModal
            imageSrc={cropImageSrc}
            title="Crop & Resize Item Photo"
            initialAspect={1}
            onClose={() => setCropImageSrc(null)}
            onCropComplete={async (croppedData) => {
              setCropImageSrc(null);
              if (croppedData?.file) {
                await handleUploadWebImage([croppedData.file]);
              }
            }}
          />
        )}
      </>
  );
}
